import { control, curve, display, readout, type PhysicsDomain } from './domainTypes';

/** NIST CODATA 2022 measured value. Classroom Earth parameters below are rounded. */
export const GRAVITATIONAL_CONSTANT = 6.6743e-11;
export const EARTH_MASS = 5.972e24;
export const EARTH_RADIUS = 6.371e6;

export const circularGravity: PhysicsDomain = {
  id: 'circular-gravity',
  name: 'Circular Motion & Gravity Lab',
  hint: 'Compare inward acceleration, inverse-square gravity and circular orbital speed.',
  experiments: [
    {
      id: 'circular',
      name: 'Uniform circular motion',
      question: 'Can speed stay constant while velocity keeps changing?',
      controls: [
        control('mass', 'Object mass', 'kg', 0.1, 10, 1, 0.1),
        control('speed', 'Speed', 'm/s', 0, 20, 4, 0.5),
        control('radius', 'Circle radius', 'm', 0.5, 20, 2, 0.5),
        control('position', 'Position around circle', '°', 0, 360, 0, 5),
      ],
      examples: [
        {
          id: 'steady',
          name: 'Constant speed',
          values: {},
          note: 'Velocity is tangent; acceleration points towards the centre.',
        },
        {
          id: 'double-speed',
          name: 'Twice the speed',
          values: { speed: 8 },
          note: 'Doubling speed quadruples required inward force.',
        },
        {
          id: 'wide',
          name: 'Wider circle',
          values: { radius: 4 },
          note: 'At fixed speed, a larger radius reduces inward acceleration.',
        },
      ],
      assumptions:
        'Uniform circular motion of a point object. Net force is entirely radial. Position control changes the snapshot, not speed. Centripetal force describes net inward force; it is not an additional force.',
      source: 'https://openstax.org/books/university-physics-volume-1/pages/6-3-centripetal-force',
      calculate: ({ mass, speed, radius }) => {
        const acceleration = speed ** 2 / radius;
        return {
          readouts: [
            readout('Inward acceleration', acceleration, 'm/s²'),
            readout('Net inward force', mass * acceleration, 'N'),
            readout('Angular speed', speed / radius, 'rad/s'),
          ],
          summary:
            speed === 0
              ? 'The object is stationary; the required inward acceleration is zero.'
              : 'Speed is constant, but velocity changes direction. Acceleration points inward.',
          working: [
            'a = v² / r',
            `a = ${speed}² / ${radius} = ${display(acceleration)} m/s²`,
            `Fnet = m a = ${display(mass * acceleration)} N`,
            ...(speed === 0
              ? ['A stationary object has no finite orbital period.']
              : [`T = 2πr / v = ${display((2 * Math.PI * radius) / speed)} s`]),
          ],
          plot: curve(20, (v) => (mass * v ** 2) / radius),
          xLabel: 'Speed (m/s)',
          yLabel: 'Required inward force (N)',
        };
      },
      prediction: {
        question: 'An object travels around a circle at constant speed. What is its acceleration?',
        options: ['Towards the centre', 'Zero', 'Along the tangent'],
        answer: 0,
        why: 'Velocity changes direction even when speed stays constant.',
      },
      task: 'Compare doubling radius and doubling speed. Predict which change has the larger effect on required force.',
    },
    {
      id: 'orbit',
      name: 'Circular Earth orbit',
      question: 'Does a heavier satellite need a different speed at the same orbital radius?',
      controls: [
        control('height', 'Height above Earth', 'km', 200, 50000, 400, 100),
        control('mass', 'Satellite mass', 'kg', 100, 5000, 1000, 100),
        control('position', 'Orbital position', '°', 0, 360, 0, 5),
      ],
      examples: [
        {
          id: 'low',
          name: 'Low circular orbit',
          values: {},
          note: 'Radius is measured from the centre, not from the surface.',
        },
        {
          id: 'high',
          name: 'High circular orbit',
          values: { height: 35786 },
          note: 'A higher circular orbit has a lower speed and a longer period.',
        },
        {
          id: 'heavy',
          name: 'Heavier satellite',
          values: { mass: 2000 },
          note: 'Mass changes force and energy, but cancels from orbital speed.',
        },
      ],
      assumptions:
        'Spherical Earth, circular orbit, point satellite, no atmosphere or other bodies. G = 6.67430 × 10⁻¹¹ m³/(kg s²), M = 5.972 × 10²⁴ kg, R = 6371 km. Earth values are rounded classroom parameters.',
      source:
        'https://openstax.org/books/university-physics-volume-1/pages/13-4-satellite-orbits-and-energy',
      calculate: ({ height, mass }) => {
        const r = EARTH_RADIUS + height * 1000;
        const mu = GRAVITATIONAL_CONSTANT * EARTH_MASS;
        const speed = Math.sqrt(mu / r);
        const gravity = mu / r ** 2;
        return {
          readouts: [
            readout('Centre-to-centre radius', r / 1000, 'km'),
            readout('Circular orbital speed', speed / 1000, 'km/s'),
            readout('Orbital period', (2 * Math.PI * r) / speed / 3600, 'h'),
            readout('Gravity acceleration', gravity, 'm/s²'),
            readout('Gravity force', mass * gravity, 'N'),
          ],
          summary:
            'At a fixed circular orbital radius, satellite mass does not change the required orbital speed.',
          working: [
            `r = R + h = ${display(r)} m`,
            'G M m / r² = m v² / r',
            `v = √(G M / r) = ${display(speed)} m/s`,
            `T = 2πr / v = ${display((2 * Math.PI * r) / speed)} s`,
          ],
          plot: curve(50000, (h) => Math.sqrt(mu / (EARTH_RADIUS + h * 1000)) / 1000),
          xLabel: 'Height (km)',
          yLabel: 'Orbital speed (km/s)',
        };
      },
      prediction: {
        question:
          'Double satellite mass at the same circular orbital radius. What happens to required speed?',
        options: ['It stays the same', 'It doubles', 'It halves'],
        answer: 0,
        why: 'Satellite mass cancels when gravity supplies the required inward force.',
      },
      task: 'Find two orbital radii with a ratio of 2. Predict the gravity ratio and speed ratio, then check them.',
    },
  ],
};
